/**
 * Satış/Stok modülü (şartname #8, #11–#22).
 *
 * Emanete konu OLMAYAN satılabilir ürünlerin (Lastik / Jant / Diğer Malzeme)
 * stok takibi. Tek sayfada sekmeler hâlinde:
 *   • Stok        → ürün listesi + filtreler (tip, stok durumu, depo, arama)
 *   • Satışlar    → satış kayıtları ve tahsilat kırılımı
 *   • Hareketler  → stok hareket defteri (giriş/satış/iade/kullanım)
 *   • Satıcılar   → satıcı listesi
 * Üstteki iki buton: "Stok Girişi" ve "Stok Çıkışı".
 *
 * TASARIM: mevcut ekranlarla aynı bileşenler (Card / Tabs / Dialog / Combobox /
 * Select / Badge) ve aynı görsel dil kullanılır; yeni bir tasarım sistemi
 * getirilmez.
 *
 * KDV: her fiyat alanı "tutar + KDV Dahil/Hariç + KDV oranı" üçlüsüyle girilir.
 * Oran varsayılan %20'dir ve kullanıcı işlem sırasında değiştirebilir; sonuç
 * anında hesaplanıp gösterilir (hesabın kaynağı backend'dir, buradaki gösterim
 * yalnızca önizlemedir).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, notifyError, API_BASE } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { fmtMoney } from "../lib/currency";
import { compressImage } from "../lib/imageCompress";
import { PAYMENT_METHODS, paymentMethodLabel } from "../lib/paymentMethods";
import Combobox from "../components/Combobox";
import CustomerFormDialog from "../components/CustomerFormDialog";
import ImageLightbox from "../components/ImageLightbox";
import ProductViewDialog from "../components/ProductViewDialog";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Boxes,
  Plus,
  Search,
  Loader2,
  Trash2,
  PackagePlus,
  PackageMinus,
  AlertTriangle,
  Truck,
  Pencil,
  Eye,
  Camera,
  Image as ImageIcon,
} from "lucide-react";

const DEFAULT_KDV = 20;
const TODAY = () => new Date().toISOString().slice(0, 10);

const emptyPrice = () => ({ amount: "", kdv_included: false, kdv_rate: DEFAULT_KDV });

const emptyProduct = (warehouseId = "") => ({
  product_type: "lastik",
  // Sıfır / İkinci El — varsayılan "Sıfır" (backend ProductIn ile aynı).
  condition: "sifir",
  name: "",
  category: "otomobil",
  brand: "",
  model: "",
  quantity: 1,
  warehouse_id: warehouseId,
  critical_quantity: 0,
  purchase: emptyPrice(),
  sale: emptyPrice(),
  use_profit_rate: false,
  profit_rate: "",
  // Lastik
  size: "",
  pattern: "",
  season: "kis",
  dot: "",
  tread_depth_mm: "",
  production_date: "",
  // Jant
  rim_diameter_inch: "",
  rim_pcd: "",
  rim_width_j: "",
  rim_offset_et: "",
  rim_center_bore: "",
  rim_material: "",
  notes: "",
  // Ürün fotoğrafı: dosya seçilince hemen /media'ya yüklenir, dönen id burada
  // tutulur ve stok girişi kaydedilirken kalemle birlikte gönderilir.
  photo_media_id: "",
  photo_preview: "",
});

/** Önizleme hesabı — backend ile aynı kural (bkz. repo.vat_split). */
function vatSplit(amount, rate, included) {
  const amt = Number(amount) || 0;
  const r = Number(rate);
  const pct = Number.isFinite(r) ? Math.min(Math.max(r, 0), 100) : DEFAULT_KDV;
  const factor = 1 + pct / 100;
  if (included) return { net: amt / factor, gross: amt };
  return { net: amt, gross: amt * factor };
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

// ---------------------------------------------------------------------------
// Ürün fotoğrafı seçimi (bilgisayardan dosya / mobilde kamera)
// ---------------------------------------------------------------------------
// AKIŞ: dosya seçilir → istemcide küçültülür (lib/imageCompress) → POST /media
// ile hemen yüklenir → dönen id kalemde tutulur. Böylece kullanıcı kaydetmeden
// önce önizlemeyi görür ve büyük dosya form gönderimini bekletmez.
// Boyut/MIME doğrulaması ayrıca backend'de yapılır (backend/media.py).
function PhotoPicker({ value, preview, onChange, testId }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // aynı dosya tekrar seçilebilsin
    if (!file) return;
    setBusy(true);
    let localPreview = "";
    try {
      const compressed = await compressImage(file);
      const fd = new FormData();
      fd.append("file", compressed);
      fd.append("kind", "product_photo");
      const { data } = await api.post("/media", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      localPreview = URL.createObjectURL(compressed);
      onChange({ photo_media_id: data.id, photo_preview: localPreview });
    } catch (err) {
      if (localPreview) URL.revokeObjectURL(localPreview);
      notifyError(err);
    } finally {
      setBusy(false);
    }
  };

  const clear = () => {
    if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview);
    onChange({ photo_media_id: "", photo_preview: "" });
  };

  return (
    <div className="space-y-1.5">
      <Label className="label-eyebrow">Ürün Fotoğrafı</Label>
      <input
        ref={inputRef}
        type="file"
        // accept="image/*" + capture YOK: mobilde işletim sistemi seçicisi
        // "Fotoğraf Çek / Galeri / Dosya" seçeneklerinin ÜÇÜNÜ birlikte sunar.
        // `capture` verilseydi mobilde yalnızca kamera açılır, galeriden veya
        // bilgisayardan seçim yapılamazdı. Gerçek tür backend'de doğrulanır.
        accept="image/*"
        onChange={pick}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      <div className="flex items-center gap-3">
        {preview || value ? (
          <img
            src={preview || `${API_BASE}/media/${value}`}
            alt="Ürün fotoğrafı"
            className="h-16 w-16 rounded-sm border border-border object-cover shrink-0"
            data-testid={`${testId}-preview`}
          />
        ) : (
          <div className="h-16 w-16 rounded-sm border border-dashed border-border flex items-center justify-center text-muted-foreground shrink-0">
            <ImageIcon className="h-5 w-5" />
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            className="rounded-sm"
            data-testid={`${testId}-btn`}
          >
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
            ) : (
              <Camera className="h-3.5 w-3.5 mr-1" />
            )}
            {value ? "Değiştir" : "Fotoğraf Ekle"}
          </Button>
          {value && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={clear}
              className="rounded-sm border-destructive/40 text-destructive hover:bg-destructive/10"
              data-testid={`${testId}-clear`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
      <p className="text-[10px] text-muted-foreground">
        JPEG / PNG / WebP — yüklemeden önce en fazla 1280 px ve 150 KB olacak
        şekilde otomatik küçültülür.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KDV'li fiyat girişi
// ---------------------------------------------------------------------------
function PriceFields({ label, value, onChange, currency, testId, disabled }) {
  const { gross, net } = vatSplit(value.amount, value.kdv_rate, value.kdv_included);
  return (
    <div className="space-y-1.5">
      <Label className="label-eyebrow">{label}</Label>
      <div className="grid grid-cols-[1fr_auto_auto] gap-2">
        <Input
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={value.amount}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, amount: e.target.value })}
          className="rounded-sm font-mono"
          placeholder="0,00"
          data-testid={`${testId}-amount`}
        />
        <Select
          value={value.kdv_included ? "dahil" : "haric"}
          onValueChange={(v) => onChange({ ...value, kdv_included: v === "dahil" })}
          disabled={disabled}
        >
          <SelectTrigger className="rounded-sm w-28" data-testid={`${testId}-kdv-mode`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="haric">KDV Hariç</SelectItem>
            <SelectItem value="dahil">KDV Dahil</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative">
          <Input
            type="number"
            inputMode="decimal"
            min="0"
            max="100"
            step="0.01"
            value={value.kdv_rate}
            disabled={disabled}
            onChange={(e) => onChange({ ...value, kdv_rate: e.target.value })}
            className="rounded-sm font-mono w-24 pr-6"
            data-testid={`${testId}-kdv-rate`}
          />
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
            %
          </span>
        </div>
      </div>
      <div className="text-[11px] text-muted-foreground font-mono">
        KDV Hariç: {fmtMoney(round2(net), currency)} • KDV Dahil:{" "}
        <b>{fmtMoney(round2(gross), currency)}</b>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Satıcı formu (ekle / düzenle) — şartname #11
//
// Stok Girişi ekranındaki "Ekle" butonu ve Satıcılar sekmesindeki "Düzenle"
// butonu AYNI formu kullanır → iki yerdeki alanlar birebir aynı kalır.
//
// ÖNEMLİ: Radix Dialog içeriği portal'a taşınsa da React ağacında çağıranın
// altındadır; submit olayı React'in sentetik sisteminde YUKARI BALONLANIR.
// Bu form Stok Girişi'nin dış `<form>`u içinde render edildiği için
// `stopPropagation()` şart — aksi hâlde satıcı kaydedilirken dış form da
// submit edilip "Satıcı seçin" uyarısı çıkıyordu.
// ---------------------------------------------------------------------------
const emptySupplierForm = () => ({
  full_name: "",
  contact_name: "",
  phone: "",
  email: "",
  address: "",
  tax_no: "",
  tax_office: "",
});

function supplierToForm(s) {
  if (!s) return emptySupplierForm();
  return {
    full_name: s.full_name || "",
    contact_name: s.contact_name || "",
    phone: s.phone || "",
    email: s.email || "",
    address: s.address || "",
    tax_no: s.tax_no || "",
    tax_office: s.tax_office || "",
  };
}

function SupplierFormDialog({ open, onOpenChange, supplier, onSaved }) {
  const editingId = supplier?.id || null;
  const [form, setForm] = useState(emptySupplierForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(supplierToForm(supplier));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingId]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!form.full_name.trim()) {
      toast.error("Satıcı adı girin");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        contact_name: form.contact_name.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        address: form.address.trim() || null,
        tax_no: form.tax_no.trim() || null,
        tax_office: form.tax_office.trim() || null,
      };
      const { data } = editingId
        ? await api.put(`/sales-stock/suppliers/${editingId}`, payload)
        : await api.post("/sales-stock/suppliers", payload);
      toast.success(editingId ? "Satıcı güncellendi" : "Satıcı eklendi");
      onOpenChange(false);
      await onSaved?.(data);
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            {editingId ? "Satıcı Düzenle" : "Yeni Satıcı"}
          </DialogTitle>
          <DialogDescription>
            Firma bilgileri zorunlu değildir; sonradan Satıcılar sekmesinden
            tamamlanabilir.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="supplier-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="label-eyebrow">Satıcı / Firma Adı *</Label>
              <Input
                value={form.full_name}
                onChange={(e) => set({ full_name: e.target.value })}
                className="rounded-sm"
                data-testid="supplier-name"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Yetkili Ad / Soyad</Label>
              <Input
                value={form.contact_name}
                onChange={(e) => set({ contact_name: e.target.value })}
                className="rounded-sm"
                data-testid="supplier-contact-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Telefon</Label>
              <Input
                value={form.phone}
                onChange={(e) => set({ phone: e.target.value })}
                className="rounded-sm font-mono"
                data-testid="supplier-phone"
              />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="label-eyebrow">E-posta</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => set({ email: e.target.value })}
                className="rounded-sm"
                data-testid="supplier-email"
              />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="label-eyebrow">Firma Adresi</Label>
              <Textarea
                value={form.address}
                onChange={(e) => set({ address: e.target.value })}
                className="rounded-sm"
                rows={2}
                data-testid="supplier-address"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Vergi Numarası</Label>
              <Input
                value={form.tax_no}
                onChange={(e) => set({ tax_no: e.target.value })}
                className="rounded-sm font-mono"
                data-testid="supplier-tax-no"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Vergi Dairesi</Label>
              <Input
                value={form.tax_office}
                onChange={(e) => set({ tax_office: e.target.value })}
                className="rounded-sm"
                data-testid="supplier-tax-office"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">
              İptal
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
              data-testid="supplier-submit"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingId ? "Güncelle" : "Kaydet"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Satıcı seçimi + aynı ekrandan ekleme (şartname #11)
// ---------------------------------------------------------------------------
function SupplierPicker({ suppliers, value, onChange, onCreated }) {
  const [open, setOpen] = useState(false);

  const items = suppliers.map((s) => ({
    value: s.id,
    label: s.phone ? `${s.full_name} — ${s.phone}` : s.full_name,
    keywords: [s.full_name, s.contact_name, s.phone, s.tax_no, s.tax_office].filter(Boolean),
  }));

  return (
    <div className="space-y-1.5 min-w-0">
      <Label className="label-eyebrow">Satıcı *</Label>
      <div className="flex gap-2 min-w-0">
        <Combobox
          value={value}
          onChange={onChange}
          items={items}
          placeholder="Satıcı seçin"
          searchPlaceholder="Ad, telefon veya vergi no ara..."
          emptyText="Satıcı bulunamadı"
          testId="stock-supplier-select"
          className="flex-1 min-w-0"
        />
        <Button
          type="button"
          variant="outline"
          className="rounded-sm shrink-0 h-10"
          onClick={() => setOpen(true)}
          data-testid="stock-supplier-add"
        >
          <Plus className="h-4 w-4 mr-1" /> Ekle
        </Button>
      </div>

      <SupplierFormDialog
        open={open}
        onOpenChange={setOpen}
        supplier={null}
        onSaved={(created) => onCreated(created.id)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Müşteri seçimi + aynı ekrandan ekleme (şartname #17)
//
// Ekleme modalı Müşteriler ekranındakiyle AYNI bileşendir
// (components/CustomerFormDialog) → alanlar birebir aynı.
// ---------------------------------------------------------------------------
function CustomerPicker({ customers, value, onChange, onCreated }) {
  const [open, setOpen] = useState(false);

  const items = customers.map((c) => ({
    value: c.id,
    label: `${c.full_name} — ${c.phone}`,
    keywords: [c.full_name, c.phone, ...(c.vehicles || []).map((v) => v.plate)].filter(Boolean),
  }));

  return (
    <div className="space-y-1.5 min-w-0">
      <Label className="label-eyebrow">Müşteri *</Label>
      <div className="flex gap-2 min-w-0">
        <Combobox
          value={value}
          onChange={onChange}
          items={items}
          placeholder="Müşteri seçin"
          searchPlaceholder="Ad, telefon veya plaka ara..."
          emptyText="Müşteri bulunamadı"
          testId="sale-customer-select"
          className="flex-1 min-w-0"
        />
        <Button
          type="button"
          variant="outline"
          className="rounded-sm shrink-0 h-10"
          onClick={() => setOpen(true)}
          data-testid="sale-customer-add"
        >
          <Plus className="h-4 w-4 mr-1" /> Ekle
        </Button>
      </div>

      <CustomerFormDialog
        open={open}
        onOpenChange={setOpen}
        customer={null}
        onSaved={(created) => onCreated(created.id)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stok giriş kalemi formu (ürün tipine göre alanlar)
// ---------------------------------------------------------------------------
function ProductFields({ item, index, onChange, onRemove, canRemove, meta, warehouses, currency }) {
  const set = (patch) => onChange(index, { ...item, ...patch });
  const isTyre = item.product_type === "lastik";
  const isRim = item.product_type === "jant";
  const purchaseNet = vatSplit(item.purchase.amount, item.purchase.kdv_rate, item.purchase.kdv_included).net;
  const computedSaleNet = item.use_profit_rate
    ? purchaseNet * (1 + (Number(item.profit_rate) || 0) / 100)
    : null;

  return (
    <Card className="rounded-sm border-border p-4 space-y-4" data-testid={`stock-item-${index}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="rounded-sm font-mono text-[10px]">
            #{index + 1}
          </Badge>
          <div className="w-44">
            <Select value={item.product_type} onValueChange={(v) => set({ product_type: v })}>
              <SelectTrigger className="rounded-sm h-9" data-testid={`stock-item-type-${index}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(meta.product_types || []).map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Sıfır / İkinci El — ürün tipinden bağımsız, her tipte sorulur. */}
          <div className="w-36">
            <Select
              value={item.condition || "sifir"}
              onValueChange={(v) => set({ condition: v })}
            >
              <SelectTrigger className="rounded-sm h-9" data-testid={`stock-item-condition-${index}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(meta.conditions || []).map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {canRemove && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-sm border-destructive/40 text-destructive hover:bg-destructive/10"
            onClick={() => onRemove(index)}
            data-testid={`stock-item-remove-${index}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {/* Tip bazlı alanlar */}
      {item.product_type === "diger" ? (
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="space-y-1.5 sm:col-span-3">
            <Label className="label-eyebrow">Malzeme Adı *</Label>
            <Input
              value={item.name}
              onChange={(e) => set({ name: e.target.value })}
              className="rounded-sm"
              data-testid={`stock-item-name-${index}`}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Marka</Label>
            <Input value={item.brand} onChange={(e) => set({ brand: e.target.value })} className="rounded-sm" />
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Model</Label>
            <Input value={item.model} onChange={(e) => set({ model: e.target.value })} className="rounded-sm" />
          </div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Kategori *</Label>
            <Select value={item.category || ""} onValueChange={(v) => set({ category: v })}>
              <SelectTrigger className="rounded-sm" data-testid={`stock-item-category-${index}`}>
                <SelectValue placeholder="Seçin" />
              </SelectTrigger>
              <SelectContent>
                {(meta.categories || []).map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Marka *</Label>
            <Input
              value={item.brand}
              onChange={(e) => set({ brand: e.target.value })}
              className="rounded-sm"
              data-testid={`stock-item-brand-${index}`}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Model</Label>
            <Input value={item.model} onChange={(e) => set({ model: e.target.value })} className="rounded-sm" />
          </div>

          {isTyre && (
            <>
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Ebat *</Label>
                <Input
                  value={item.size}
                  onChange={(e) => set({ size: e.target.value })}
                  placeholder="205/55R16"
                  className="rounded-sm font-mono"
                  data-testid={`stock-item-size-${index}`}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Sezon *</Label>
                <Select value={item.season || ""} onValueChange={(v) => set({ season: v })}>
                  <SelectTrigger className="rounded-sm" data-testid={`stock-item-season-${index}`}>
                    <SelectValue placeholder="Seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {(meta.seasons || []).map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Desen</Label>
                <Input value={item.pattern} onChange={(e) => set({ pattern: e.target.value })} className="rounded-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Üretim Tarihi</Label>
                <Input
                  value={item.production_date}
                  onChange={(e) => set({ production_date: e.target.value })}
                  placeholder="2024/12"
                  className="rounded-sm font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="label-eyebrow">DOT</Label>
                <Input value={item.dot} onChange={(e) => set({ dot: e.target.value })} className="rounded-sm font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Diş Derinliği (mm)</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  value={item.tread_depth_mm}
                  onChange={(e) => set({ tread_depth_mm: e.target.value })}
                  className="rounded-sm font-mono"
                />
              </div>
            </>
          )}

          {isRim && (
            <>
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Jant Çapı (İnç) *</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  value={item.rim_diameter_inch}
                  onChange={(e) => set({ rim_diameter_inch: e.target.value })}
                  className="rounded-sm font-mono"
                  data-testid={`stock-item-rim-diameter-${index}`}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Bijon Aralığı (PCD)</Label>
                <Input
                  value={item.rim_pcd}
                  onChange={(e) => set({ rim_pcd: e.target.value })}
                  placeholder="5x112"
                  className="rounded-sm font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Genişlik / J Değeri</Label>
                <Input
                  value={item.rim_width_j}
                  onChange={(e) => set({ rim_width_j: e.target.value })}
                  placeholder="7.5J"
                  className="rounded-sm font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Ofset (ET)</Label>
                <Input
                  value={item.rim_offset_et}
                  onChange={(e) => set({ rim_offset_et: e.target.value })}
                  placeholder="ET45"
                  className="rounded-sm font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Göbek Çapı (CB)</Label>
                <Input
                  value={item.rim_center_bore}
                  onChange={(e) => set({ rim_center_bore: e.target.value })}
                  placeholder="66.6"
                  className="rounded-sm font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Malzeme Türü</Label>
                <Input
                  value={item.rim_material}
                  onChange={(e) => set({ rim_material: e.target.value })}
                  placeholder="Alüminyum / Çelik"
                  className="rounded-sm"
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* Adet / depo / kritik stok */}
      <div className="grid sm:grid-cols-3 gap-3 border-t border-border pt-3">
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Adet *</Label>
          <Input
            type="number"
            min="1"
            value={item.quantity}
            onChange={(e) => set({ quantity: e.target.value })}
            className="rounded-sm font-mono"
            data-testid={`stock-item-quantity-${index}`}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Depo *</Label>
          <Select value={item.warehouse_id || ""} onValueChange={(v) => set({ warehouse_id: v })}>
            <SelectTrigger className="rounded-sm" data-testid={`stock-item-warehouse-${index}`}>
              <SelectValue placeholder="Seçin" />
            </SelectTrigger>
            <SelectContent>
              {warehouses.map((w) => (
                <SelectItem key={w.id} value={w.id}>
                  {w.name}
                  {w.is_default_stock ? " (varsayılan)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Kritik Stok Miktarı</Label>
          <Input
            type="number"
            min="0"
            value={item.critical_quantity}
            onChange={(e) => set({ critical_quantity: e.target.value })}
            className="rounded-sm font-mono"
            data-testid={`stock-item-critical-${index}`}
          />
          <p className="text-[10px] text-muted-foreground">Stokta bulunması gereken minimum adet</p>
        </div>
      </div>

      {/* Ürün fotoğrafı */}
      <div className="border-t border-border pt-3">
        <PhotoPicker
          value={item.photo_media_id}
          preview={item.photo_preview}
          onChange={(patch) => set(patch)}
          testId={`stock-item-photo-${index}`}
        />
      </div>

      {/* Fiyatlar */}
      <div className="grid lg:grid-cols-2 gap-4 border-t border-border pt-3">
        <PriceFields
          label="Alış Fiyatı (birim)"
          value={item.purchase}
          onChange={(v) => set({ purchase: v })}
          currency={currency}
          testId={`stock-item-purchase-${index}`}
        />
        <div className="space-y-2">
          <PriceFields
            label="Hedeflenen Satış Fiyatı (birim)"
            value={item.sale}
            onChange={(v) => set({ sale: v })}
            currency={currency}
            testId={`stock-item-sale-${index}`}
            disabled={item.use_profit_rate}
          />
          <div className="flex items-center gap-2">
            <Checkbox
              id={`profit-${index}`}
              checked={item.use_profit_rate}
              onCheckedChange={(v) => set({ use_profit_rate: v === true })}
              data-testid={`stock-item-use-profit-${index}`}
            />
            <Label htmlFor={`profit-${index}`} className="text-xs font-normal cursor-pointer">
              Alış fiyatı üzerinden yüzde kâr oranı kullan
            </Label>
          </div>
          {item.use_profit_rate && (
            <div className="flex items-end gap-3">
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Kâr Oranı (%)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={item.profit_rate}
                  onChange={(e) => set({ profit_rate: e.target.value })}
                  className="rounded-sm font-mono w-28"
                  data-testid={`stock-item-profit-rate-${index}`}
                />
              </div>
              <div className="text-[11px] text-muted-foreground font-mono pb-1.5">
                Satış (KDV hariç): <b>{fmtMoney(round2(computedSaleNet), currency)}</b>
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Ana sayfa
// ---------------------------------------------------------------------------
export default function SalesStock() {
  const { defaultCurrency, defaultStockWarehouseId, canManage } = useAuth();
  const currency = defaultCurrency;
  // Müşteri profilindeki "Yeni Satış" butonu buraya `?customer=<id>` ile gelir;
  // stok çıkışı diyaloğu Satış tipinde ve müşteri seçili olarak açılır
  // ("Yeni Emanet" → /emanetler?customer=... ile aynı desen).
  const [searchParams, setSearchParams] = useSearchParams();
  const presetCustomerId = searchParams.get("customer") || "";

  const [meta, setMeta] = useState({});
  const [warehouses, setWarehouses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [movements, setMovements] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("stok");

  // Stok listesi filtreleri
  const [fType, setFType] = useState("all");
  const [fStock, setFStock] = useState("all");
  const [fWarehouse, setFWarehouse] = useState("all");
  const [q, setQ] = useState("");

  // Diyaloglar
  const [entryOpen, setEntryOpen] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  const [deleteSale, setDeleteSale] = useState(null);
  const [editProduct, setEditProduct] = useState(null);
  const [viewProduct, setViewProduct] = useState(null);
  const [deleteProduct, setDeleteProduct] = useState(null);
  // { src, alt } — fotoğraf büyütme (lightbox)
  const [zoomPhoto, setZoomPhoto] = useState(null);
  // { supplier: <satıcı|null> } → null ise "Yeni Satıcı", dolu ise düzenleme.
  const [supplierDialog, setSupplierDialog] = useState(null);

  const stockWarehouseId = useMemo(() => {
    if (defaultStockWarehouseId) return defaultStockWarehouseId;
    const def = warehouses.find((w) => w.is_default_stock) || warehouses.find((w) => w.is_default);
    return def?.id || warehouses[0]?.id || "";
  }, [defaultStockWarehouseId, warehouses]);

  const loadRefData = useCallback(async () => {
    try {
      const [m, w, s, c] = await Promise.all([
        api.get("/sales-stock/meta"),
        api.get("/warehouses"),
        api.get("/sales-stock/suppliers"),
        api.get("/customers"),
      ]);
      setMeta(m.data);
      setWarehouses(w.data);
      setSuppliers(s.data);
      setCustomers(c.data);
    } catch (e) {
      notifyError(e);
    }
  }, []);

  const loadProducts = useCallback(async () => {
    const p = new URLSearchParams();
    if (fType !== "all") p.set("product_type", fType);
    if (fStock !== "all") p.set("stock", fStock);
    if (fWarehouse !== "all") p.set("warehouse_id", fWarehouse);
    if (q.trim()) p.set("q", q.trim());
    const { data } = await api.get(`/sales-stock/products?${p.toString()}`);
    setProducts(data);
  }, [fType, fStock, fWarehouse, q]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sl, mv, en] = await Promise.all([
        api.get("/sales-stock/sales"),
        api.get("/sales-stock/movements"),
        api.get("/sales-stock/stock-entries"),
      ]);
      setSales(sl.data);
      setMovements(mv.data);
      setEntries(en.data);
      await loadProducts();
    } catch (e) {
      notifyError(e);
    } finally {
      setLoading(false);
    }
  }, [loadProducts]);

  useEffect(() => {
    (async () => {
      await loadRefData();
      await loadAll();
      // Referans veriler yüklendikten SONRA aç: müşteri seçim listesi hazır olsun.
      if (presetCustomerId) setExitOpen(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadProducts().catch(notifyError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fType, fStock, fWarehouse]);

  const refreshSuppliers = async (selectId) => {
    const { data } = await api.get("/sales-stock/suppliers");
    setSuppliers(data);
    return selectId;
  };

  const refreshCustomers = async (selectId) => {
    const { data } = await api.get("/customers");
    setCustomers(data);
    return selectId;
  };

  const criticalCount = products.filter((p) => p.is_critical).length;
  const totalStock = products.reduce((a, p) => a + (p.quantity || 0), 0);
  const stockValue = products.reduce(
    (a, p) => a + (p.quantity || 0) * (p.purchase_price_net || 0),
    0
  );

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto space-y-6" data-testid="sales-stock-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow text-amber-600">Satılabilir Ürünler</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">
            Satış / Stok
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Emanete konu olmayan lastik, jant ve diğer malzemelerin stok takibi,
            satış ve tahsilat işlemleri.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={() => setEntryOpen(true)}
            className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
            data-testid="stock-entry-btn"
          >
            <PackagePlus className="h-4 w-4 mr-2" /> Stok Girişi
          </Button>
          <Button
            onClick={() => setExitOpen(true)}
            variant="outline"
            className="rounded-sm"
            data-testid="stock-exit-btn"
          >
            <PackageMinus className="h-4 w-4 mr-2" /> Stok Çıkışı
          </Button>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Ürün Çeşidi" value={products.length} icon={Boxes} />
        <Kpi label="Toplam Stok (Adet)" value={totalStock} icon={Boxes} accent />
        <Kpi
          label="Stok Maliyeti (KDV hariç)"
          value={fmtMoney(round2(stockValue), currency, { maximumFractionDigits: 0 })}
          icon={Truck}
        />
        <Kpi
          label="Kritik Stok"
          value={criticalCount}
          icon={AlertTriangle}
          danger={criticalCount > 0}
        />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-sm bg-muted flex-wrap h-auto">
          <TabsTrigger value="stok" className="rounded-sm" data-testid="tab-stok">Stok</TabsTrigger>
          <TabsTrigger value="satislar" className="rounded-sm" data-testid="tab-satislar">Satışlar</TabsTrigger>
          <TabsTrigger value="hareketler" className="rounded-sm" data-testid="tab-hareketler">Hareketler</TabsTrigger>
          <TabsTrigger value="girisler" className="rounded-sm" data-testid="tab-girisler">Stok Girişleri</TabsTrigger>
          <TabsTrigger value="saticilar" className="rounded-sm" data-testid="tab-saticilar">Satıcılar</TabsTrigger>
        </TabsList>

        {/* ------------------------------ STOK ------------------------------ */}
        <TabsContent value="stok" className="mt-4 space-y-4">
          <Card className="rounded-sm border-border p-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label className="label-eyebrow">Ürün Tipi</Label>
                <Select value={fType} onValueChange={setFType}>
                  <SelectTrigger className="w-40 rounded-sm h-9" data-testid="filter-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    {(meta.product_types || []).map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="label-eyebrow">Stok Durumu</Label>
                <Select value={fStock} onValueChange={setFStock}>
                  <SelectTrigger className="w-32 rounded-sm h-9" data-testid="filter-stock">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    <SelectItem value="in">Var</SelectItem>
                    <SelectItem value="out">Yok</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="label-eyebrow">Depo</Label>
                <Select value={fWarehouse} onValueChange={setFWarehouse}>
                  <SelectTrigger className="w-44 rounded-sm h-9" data-testid="filter-warehouse">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    {warehouses.map((w) => (
                      <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="relative flex-1 min-w-[180px]">
                <Label className="label-eyebrow">Ara</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && loadProducts().catch(notifyError)}
                    placeholder="Ürün, marka, ebat..."
                    className="pl-10 h-9 rounded-sm"
                    data-testid="filter-q"
                  />
                </div>
              </div>
              <Button
                onClick={() => loadProducts().catch(notifyError)}
                className="rounded-sm bg-foreground text-background hover:bg-foreground/90 h-9"
                data-testid="filter-apply"
              >
                Uygula
              </Button>
            </div>
          </Card>

          {loading ? (
            <div className="text-center py-16"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
          ) : products.length === 0 ? (
            <Card className="p-12 text-center rounded-sm">
              <Boxes className="h-10 w-10 mx-auto text-muted-foreground" />
              <div className="font-display font-bold text-lg mt-3">Stokta ürün yok</div>
              <p className="text-sm text-muted-foreground mt-2">
                "Stok Girişi" ile ilk ürünlerinizi ekleyin.
              </p>
            </Card>
          ) : (
            <Card className="rounded-sm border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-foreground text-background">
                    <tr>
                      <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Ürün</th>
                      <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Tip</th>
                      <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Satıcı / Fatura</th>
                      <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Depo</th>
                      <th className="text-right px-4 py-3 label-eyebrow !text-gray-300">Stok</th>
                      <th className="text-right px-4 py-3 label-eyebrow !text-gray-300">Kritik</th>
                      <th className="text-right px-4 py-3 label-eyebrow !text-gray-300">Alış (KDV dahil)</th>
                      <th className="text-right px-4 py-3 label-eyebrow !text-gray-300">Satış (KDV dahil)</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {products.map((p) => (
                      <tr
                        key={p.id}
                        className={p.is_critical ? "bg-red-50/60" : ""}
                        data-testid={`product-row-${p.id}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {/* Stok girişinde eklenen fotoğraf; yoksa yer tutmaz */}
                            {p.photo_media_id && (
                              <button
                                type="button"
                                onClick={() =>
                                  setZoomPhoto({
                                    src: `${API_BASE}/media/${p.photo_media_id}`,
                                    alt: p.name,
                                  })
                                }
                                className="shrink-0 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                title="Büyütmek için tıklayın"
                                data-testid={`product-photo-zoom-${p.id}`}
                              >
                                <img
                                  src={`${API_BASE}/media/${p.photo_media_id}`}
                                  alt={p.name}
                                  loading="lazy"
                                  className="h-10 w-10 rounded-sm border border-border object-cover cursor-zoom-in hover:border-foreground/50 transition-colors"
                                  data-testid={`product-photo-${p.id}`}
                                />
                              </button>
                            )}
                            {/* Marka/model/ebat gibi bilgiler tıklanabilir →
                                göz ikonuyla AYNI görüntüleme ekranını açar. */}
                            <button
                              type="button"
                              onClick={() => setViewProduct(p)}
                              className="min-w-0 text-left rounded-sm hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              title="Ürün bilgilerini görüntüle"
                              data-testid={`product-open-${p.id}`}
                            >
                              <div className="font-semibold">{p.name}</div>
                              <div className="text-xs text-muted-foreground font-mono">
                                {[p.size, p.season && seasonShort(p.season, meta), p.dot]
                                  .filter(Boolean)
                                  .join(" • ")}
                              </div>
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs">{p.product_type_label}</td>
                        <td className="px-4 py-3 text-xs">
                          <div>{p.supplier_name || "—"}</div>
                          <div className="text-muted-foreground font-mono">{p.invoice_no || "—"}</div>
                        </td>
                        <td className="px-4 py-3 text-xs font-mono">{p.warehouse_name || "—"}</td>
                        <td className="px-4 py-3 font-mono text-right font-bold">
                          {p.quantity}
                          {p.is_critical && (
                            <AlertTriangle className="h-3.5 w-3.5 inline ml-1 text-red-600" />
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-right text-muted-foreground">
                          {p.critical_quantity}
                        </td>
                        <td className="px-4 py-3 font-mono text-right">
                          {fmtMoney(p.purchase_price_gross, currency)}
                        </td>
                        <td className="px-4 py-3 font-mono text-right">
                          {fmtMoney(p.sale_price_gross, currency)}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-sm mr-1"
                            onClick={() => setViewProduct(p)}
                            data-testid={`product-view-${p.id}`}
                            aria-label="Görüntüle"
                            title="Görüntüle"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-sm"
                            onClick={() => setEditProduct(p)}
                            data-testid={`product-edit-${p.id}`}
                            aria-label="Düzenle"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          {/* Silme YALNIZCA hiç çıkış yapılmamış kayıtlarda.
                              Asıl kural backend'de (409 döner); bu sadece
                              kullanılamayacak butonu göstermemek için. */}
                          {canManage && p.can_modify && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-sm ml-1 border-destructive/40 text-destructive hover:bg-destructive/10"
                              onClick={() => setDeleteProduct(p)}
                              data-testid={`product-delete-${p.id}`}
                              aria-label="Sil"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </TabsContent>

        {/* ---------------------------- SATIŞLAR ---------------------------- */}
        <TabsContent value="satislar" className="mt-4">
          {sales.length === 0 ? (
            <Card className="p-12 text-center rounded-sm">
              <Boxes className="h-10 w-10 mx-auto text-muted-foreground" />
              <div className="font-display font-bold text-lg mt-3">Satış kaydı yok</div>
            </Card>
          ) : (
            <div className="space-y-3">
              {sales.map((s) => (
                <Card key={s.id} className="rounded-sm border-border p-4" data-testid={`sale-row-${s.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="font-display font-bold text-base tracking-tight">
                        {s.customer_name || "—"}
                        {s.vehicle_plate && (
                          <span className="ml-2 font-mono text-xs font-bold tracking-wider text-muted-foreground">
                            {s.vehicle_plate}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono mt-0.5">
                        {new Date(s.sale_date).toLocaleString("tr-TR")}
                        {s.actor_name ? ` • ${s.actor_name}` : ""}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="label-eyebrow">Toplam (KDV dahil)</div>
                      <div className="font-display font-black text-xl tracking-tighter">
                        {fmtMoney(s.total_gross, s.currency || currency)}
                      </div>
                      <div className="text-[11px] text-muted-foreground font-mono">
                        KDV: {fmtMoney(s.total_kdv, s.currency || currency)}
                      </div>
                    </div>
                    {canManage && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-sm border-destructive/40 text-destructive hover:bg-destructive/10"
                        onClick={() => setDeleteSale(s)}
                        data-testid={`sale-delete-${s.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>

                  <div className="mt-3 border-t border-border pt-3 grid lg:grid-cols-2 gap-4">
                    <div>
                      <div className="label-eyebrow mb-1.5">Ürünler</div>
                      <ul className="space-y-1 text-sm">
                        {(s.items || []).map((it) => (
                          <li key={it.id} className="flex justify-between gap-3">
                            <span className="truncate">
                              {it.product_name}{" "}
                              <span className="text-muted-foreground font-mono">×{it.quantity}</span>
                            </span>
                            <span className="font-mono shrink-0">
                              {fmtMoney(it.line_gross, s.currency || currency)}
                            </span>
                          </li>
                        ))}
                        {s.service_enabled && (
                          <li className="flex justify-between gap-3 border-t border-border/60 pt-1">
                            <span className="truncate">
                              Hizmet Bedeli
                              {s.service_description ? ` — ${s.service_description}` : ""}
                            </span>
                            <span className="font-mono shrink-0">
                              {fmtMoney(s.service_gross, s.currency || currency)}
                            </span>
                          </li>
                        )}
                      </ul>
                    </div>
                    <div>
                      <div className="label-eyebrow mb-1.5">Tahsilat</div>
                      <ul className="space-y-1 text-sm">
                        {(s.collections || []).map((c) => (
                          <li key={c.id} className="flex justify-between gap-3">
                            <span>{c.method_label || paymentMethodLabel(c.method)}</span>
                            <span className="font-mono">
                              {fmtMoney(c.amount, s.currency || currency)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* --------------------------- HAREKETLER --------------------------- */}
        <TabsContent value="hareketler" className="mt-4">
          <Card className="rounded-sm border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-foreground text-background">
                  <tr>
                    <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Tarih</th>
                    <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Hareket</th>
                    <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Ürün</th>
                    <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Depo</th>
                    <th className="text-right px-4 py-3 label-eyebrow !text-gray-300">Miktar</th>
                    <th className="text-right px-4 py-3 label-eyebrow !text-gray-300">Birim Fiyat</th>
                    <th className="text-right px-4 py-3 label-eyebrow !text-gray-300">KDV</th>
                    <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Kullanıcı</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {movements.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-muted-foreground text-sm">
                        Hareket yok
                      </td>
                    </tr>
                  ) : (
                    movements.map((m) => (
                      <tr key={m.id} data-testid={`movement-row-${m.id}`}>
                        <td className="px-4 py-2.5 font-mono text-xs">
                          {new Date(m.date).toLocaleDateString("tr-TR")}
                        </td>
                        <td className="px-4 py-2.5">
                          <Badge
                            variant="outline"
                            className={`rounded-sm text-[10px] uppercase tracking-wider ${
                              m.type === "giris"
                                ? "border-emerald-500/40 text-emerald-700"
                                : m.type === "satis"
                                ? "border-sky-500/40 text-sky-700"
                                : m.type === "iade"
                                ? "border-amber-500/40 text-amber-700"
                                : "border-slate-400/40 text-slate-600"
                            }`}
                          >
                            {m.type_label}
                          </Badge>
                        </td>
                        <td className="px-4 py-2.5">{m.product_name || "—"}</td>
                        <td className="px-4 py-2.5 text-xs font-mono">{m.warehouse_name || "—"}</td>
                        <td className="px-4 py-2.5 font-mono text-right">
                          {m.type === "giris" ? "+" : "−"}
                          {m.quantity}
                        </td>
                        <td className="px-4 py-2.5 font-mono text-right">
                          {fmtMoney(m.unit_price, currency)}
                        </td>
                        <td className="px-4 py-2.5 font-mono text-right text-xs">
                          %{m.kdv_rate} {m.kdv_included ? "(dahil)" : "(hariç)"}
                        </td>
                        <td className="px-4 py-2.5 text-xs">{m.actor_name || "—"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* ------------------------- STOK GİRİŞLERİ ------------------------- */}
        <TabsContent value="girisler" className="mt-4">
          {entries.length === 0 ? (
            <Card className="p-12 text-center rounded-sm">
              <PackagePlus className="h-10 w-10 mx-auto text-muted-foreground" />
              <div className="font-display font-bold text-lg mt-3">Stok girişi yok</div>
            </Card>
          ) : (
            <div className="space-y-3">
              {entries.map((e) => (
                <Card key={e.id} className="rounded-sm border-border p-4" data-testid={`entry-row-${e.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="font-display font-bold text-base tracking-tight">
                        {e.supplier_name || "—"}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono mt-0.5">
                        Fatura: {e.invoice_no || "—"}
                        {e.invoice_date
                          ? ` • ${new Date(e.invoice_date).toLocaleDateString("tr-TR")}`
                          : ""}
                        {e.entry_date
                          ? ` • Giriş: ${new Date(e.entry_date).toLocaleDateString("tr-TR")}`
                          : ""}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="label-eyebrow">Toplam</div>
                      <div className="font-mono font-bold">
                        {e.total_quantity} adet •{" "}
                        {fmtMoney(e.total_purchase_gross, currency)}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 border-t border-border pt-2">
                    <ul className="space-y-1 text-sm">
                      {(e.items || []).map((it) => (
                        <li key={it.id} className="flex justify-between gap-3">
                          <span className="truncate">
                            {it.name}{" "}
                            <span className="text-muted-foreground font-mono">
                              ×{it.entry_quantity} (kalan {it.quantity})
                            </span>
                          </span>
                          <span className="font-mono shrink-0">
                            {fmtMoney(it.purchase_price_gross, currency)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* --------------------------- SATICILAR --------------------------- */}
        <TabsContent value="saticilar" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button
              onClick={() => setSupplierDialog({ supplier: null })}
              className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
              data-testid="supplier-add-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Yeni Satıcı
            </Button>
          </div>
          <Card className="rounded-sm border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-foreground text-background">
                  <tr>
                    <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Satıcı</th>
                    <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Yetkili</th>
                    <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Telefon</th>
                    <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Adres</th>
                    <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Vergi No</th>
                    <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Vergi Dairesi</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {suppliers.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-muted-foreground text-sm">
                        Satıcı yok — stok girişi yaparken ekleyebilirsiniz.
                      </td>
                    </tr>
                  ) : (
                    suppliers.map((s) => (
                      <tr key={s.id} data-testid={`supplier-row-${s.id}`}>
                        <td className="px-4 py-3 font-semibold">
                          {s.full_name}
                          {s.email && (
                            <div className="text-xs text-muted-foreground font-normal">{s.email}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">{s.contact_name || "—"}</td>
                        <td className="px-4 py-3 font-mono">{s.phone || "—"}</td>
                        <td className="px-4 py-3 text-xs max-w-[220px]">{s.address || "—"}</td>
                        <td className="px-4 py-3 font-mono">{s.tax_no || "—"}</td>
                        <td className="px-4 py-3">{s.tax_office || "—"}</td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-sm"
                            onClick={() => setSupplierDialog({ supplier: s })}
                            data-testid={`supplier-edit-${s.id}`}
                            aria-label="Satıcıyı düzenle"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <StockEntryDialog
        open={entryOpen}
        onOpenChange={setEntryOpen}
        meta={meta}
        warehouses={warehouses}
        suppliers={suppliers}
        onSuppliersChanged={refreshSuppliers}
        defaultWarehouseId={stockWarehouseId}
        currency={currency}
        onSaved={loadAll}
      />

      <StockExitDialog
        open={exitOpen}
        onOpenChange={(o) => {
          setExitOpen(o);
          // Kapanışta `?customer=` temizlenir → diyalog tekrar elle açıldığında
          // müşteri seçimi boş başlar.
          if (!o && presetCustomerId) setSearchParams({}, { replace: true });
        }}
        meta={meta}
        products={products}
        suppliers={suppliers}
        customers={customers}
        entries={entries}
        onCustomersChanged={refreshCustomers}
        presetCustomerId={presetCustomerId}
        currency={currency}
        onSaved={loadAll}
      />

      <SupplierFormDialog
        open={!!supplierDialog}
        onOpenChange={(o) => !o && setSupplierDialog(null)}
        supplier={supplierDialog?.supplier || null}
        onSaved={() => refreshSuppliers()}
      />

      <ProductViewDialog
        product={viewProduct}
        onClose={() => setViewProduct(null)}
        meta={meta}
        currency={currency}
      />

      <ProductEditDialog
        product={editProduct}
        onClose={() => setEditProduct(null)}
        warehouses={warehouses}
        currency={currency}
        onSaved={loadAll}
      />

      {/* Ürün fotoğrafı büyütme — zoom in/out */}
      <ImageLightbox
        src={zoomPhoto?.src}
        alt={zoomPhoto?.alt}
        onClose={() => setZoomPhoto(null)}
      />

      <AlertDialog open={!!deleteProduct} onOpenChange={(o) => !o && setDeleteProduct(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display tracking-tight">
              Ürün kaydı silinsin mi?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <b>{deleteProduct?.name}</b> stok listesinden kaldırılacak ve bu
              ürüne ait stok giriş hareketi defterden düşülecek. Bu ürün için
              hiç satış/iade/kullanım yapılmadığı için geçmiş kayıtlar
              etkilenmez. İşlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={async (ev) => {
                ev.preventDefault();
                try {
                  await api.delete(`/sales-stock/products/${deleteProduct.id}`);
                  toast.success("Ürün kaydı silindi");
                  setDeleteProduct(null);
                  await loadAll();
                } catch (err) {
                  notifyError(err);
                }
              }}
              className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="product-delete-confirm"
            >
              Evet, Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteSale} onOpenChange={(o) => !o && setDeleteSale(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display tracking-tight">
              Satış silinsin mi?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Satıştaki ürünler stoğa geri eklenecek ve bu satışa ait tahsilatlar
              Ödemeler ekranından kaldırılacak. Stok hareketi defterinde düzeltme
              kaydı oluşturulur.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={async (ev) => {
                ev.preventDefault();
                try {
                  await api.delete(`/sales-stock/sales/${deleteSale.id}`);
                  toast.success("Satış silindi, stok geri alındı");
                  setDeleteSale(null);
                  await loadAll();
                } catch (err) {
                  notifyError(err);
                }
              }}
              className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="sale-delete-confirm"
            >
              Evet, Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function seasonShort(value, meta) {
  const found = (meta.seasons || []).find((s) => s.value === value);
  return found ? found.label : value;
}

function Kpi({ label, value, icon: Icon, accent, danger }) {
  return (
    <Card className="rounded-sm p-4 border-border">
      <div className="flex items-start justify-between">
        <div className="label-eyebrow">{label}</div>
        <div
          className={`h-8 w-8 rounded-sm flex items-center justify-center ${
            danger
              ? "bg-red-600 text-white"
              : accent
              ? "bg-amber-500 text-foreground"
              : "bg-foreground text-background"
          }`}
        >
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="font-display font-black text-2xl tracking-tighter mt-2">{value}</div>
    </Card>
  );
}

// ===========================================================================
// STOK GİRİŞİ DİYALOĞU (şartname #11–#15)
// ===========================================================================
function StockEntryDialog({
  open,
  onOpenChange,
  meta,
  warehouses,
  suppliers,
  onSuppliersChanged,
  defaultWarehouseId,
  currency,
  onSaved,
}) {
  const [supplierId, setSupplierId] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(TODAY());
  const [invoiceNo, setInvoiceNo] = useState("");
  const [entryDate, setEntryDate] = useState(TODAY());
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([emptyProduct()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setSupplierId("");
      setInvoiceDate(TODAY());
      setInvoiceNo("");
      setEntryDate(TODAY());
      setNotes("");
      setItems([emptyProduct(defaultWarehouseId)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultWarehouseId]);

  const setItem = (idx, next) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? next : it)));
  const addItem = () => setItems((prev) => [...prev, emptyProduct(defaultWarehouseId)]);
  const removeItem = (idx) =>
    setItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const total = items.reduce((acc, it) => {
    const { gross } = vatSplit(it.purchase.amount, it.purchase.kdv_rate, it.purchase.kdv_included);
    return acc + gross * (Number(it.quantity) || 0);
  }, 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!supplierId) {
      toast.error("Satıcı seçin");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        supplier_id: supplierId,
        invoice_date: invoiceDate || null,
        invoice_no: invoiceNo.trim() || null,
        entry_date: entryDate || null,
        notes: notes.trim() || null,
        items: items.map((it) => ({
          product_type: it.product_type,
          // Sıfır / İkinci El — her ürün tipinde gönderilir.
          condition: it.condition || "sifir",
          name: it.name.trim() || null,
          category: it.product_type === "diger" ? null : it.category || null,
          brand: it.brand.trim() || null,
          model: it.model.trim() || null,
          quantity: Number(it.quantity) || 1,
          warehouse_id: it.warehouse_id || null,
          critical_quantity: Number(it.critical_quantity) || 0,
          purchase: {
            amount: Number(it.purchase.amount) || 0,
            kdv_included: !!it.purchase.kdv_included,
            kdv_rate: Number(it.purchase.kdv_rate),
          },
          sale: {
            amount: Number(it.sale.amount) || 0,
            kdv_included: !!it.sale.kdv_included,
            kdv_rate: Number(it.sale.kdv_rate),
          },
          profit_rate: it.use_profit_rate ? Number(it.profit_rate) || 0 : null,
          // Lastik
          size: it.product_type === "lastik" ? it.size.trim() || null : null,
          pattern: it.product_type === "lastik" ? it.pattern.trim() || null : null,
          season: it.product_type === "lastik" ? it.season || null : null,
          dot: it.product_type === "lastik" ? it.dot.trim() || null : null,
          tread_depth_mm:
            it.product_type === "lastik" && it.tread_depth_mm !== ""
              ? Number(it.tread_depth_mm)
              : null,
          production_date:
            it.product_type === "lastik" ? it.production_date.trim() || null : null,
          // Jant
          rim_diameter_inch:
            it.product_type === "jant" && it.rim_diameter_inch !== ""
              ? Number(it.rim_diameter_inch)
              : null,
          rim_pcd: it.product_type === "jant" ? it.rim_pcd.trim() || null : null,
          rim_width_j: it.product_type === "jant" ? it.rim_width_j.trim() || null : null,
          rim_offset_et: it.product_type === "jant" ? it.rim_offset_et.trim() || null : null,
          rim_center_bore:
            it.product_type === "jant" ? it.rim_center_bore.trim() || null : null,
          rim_material: it.product_type === "jant" ? it.rim_material.trim() || null : null,
          notes: it.notes.trim() || null,
          photo_media_id: it.photo_media_id || null,
        })),
      };
      await api.post("/sales-stock/stock-entries", payload);
      toast.success("Stok girişi kaydedildi");
      onOpenChange(false);
      await onSaved();
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Stok Girişi</DialogTitle>
          <DialogDescription>
            Aynı satıcı ve fatura için birden fazla ürün ekleyebilirsiniz.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4" data-testid="stock-entry-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <SupplierPicker
              suppliers={suppliers}
              value={supplierId}
              onChange={setSupplierId}
              onCreated={async (id) => {
                await onSuppliersChanged(id);
                setSupplierId(id);
              }}
            />
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Fatura No</Label>
              <Input
                value={invoiceNo}
                onChange={(e) => setInvoiceNo(e.target.value)}
                placeholder="Zorunlu değil"
                className="rounded-sm font-mono"
                data-testid="stock-entry-invoice-no"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Fatura Tarihi</Label>
              <Input
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                className="rounded-sm"
                data-testid="stock-entry-invoice-date"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Stok Giriş Tarihi</Label>
              <Input
                type="date"
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
                className="rounded-sm"
                data-testid="stock-entry-date"
              />
              <p className="text-[10px] text-muted-foreground">Boş bırakılabilir</p>
            </div>
          </div>

          <div className="space-y-3">
            {items.map((it, i) => (
              <ProductFields
                key={i}
                item={it}
                index={i}
                onChange={setItem}
                onRemove={removeItem}
                canRemove={items.length > 1}
                meta={meta}
                warehouses={warehouses}
                currency={currency}
              />
            ))}
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={addItem}
            className="rounded-sm w-full"
            data-testid="stock-entry-add-item"
          >
            <Plus className="h-4 w-4 mr-2" /> Ürün Ekle
          </Button>

          <div className="space-y-1.5">
            <Label className="label-eyebrow">Not</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="rounded-sm"
              rows={2}
            />
          </div>

          <div className="flex items-center justify-between border-t border-border pt-3">
            <div className="label-eyebrow">Fatura Toplamı (KDV dahil)</div>
            <div className="font-display font-black text-2xl tracking-tighter" data-testid="stock-entry-total">
              {fmtMoney(round2(total), currency)}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">
              İptal
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
              data-testid="stock-entry-submit"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ===========================================================================
// STOK ÇIKIŞI DİYALOĞU (şartname #16–#19)
// ===========================================================================
function StockExitDialog({
  open,
  onOpenChange,
  meta,
  products,
  suppliers,
  customers,
  entries,
  onCustomersChanged,
  presetCustomerId,
  currency,
  onSaved,
}) {
  const [exitType, setExitType] = useState("satis");

  useEffect(() => {
    if (open) setExitType("satis");
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Stok Çıkışı</DialogTitle>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label className="label-eyebrow">Çıkış Tipi *</Label>
          <Select value={exitType} onValueChange={setExitType}>
            <SelectTrigger className="rounded-sm" data-testid="exit-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(meta.exit_types || []).map((t) => (
                <SelectItem key={t.value} value={t.value} data-testid={`exit-type-${t.value}`}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {exitType === "satis" && (
          <SaleForm
            products={products}
            customers={customers}
            onCustomersChanged={onCustomersChanged}
            presetCustomerId={presetCustomerId}
            currency={currency}
            onDone={async () => {
              onOpenChange(false);
              await onSaved();
            }}
            onCancel={() => onOpenChange(false)}
          />
        )}
        {exitType === "iade" && (
          <ReturnForm
            suppliers={suppliers}
            entries={entries}
            currency={currency}
            onDone={async () => {
              onOpenChange(false);
              await onSaved();
            }}
            onCancel={() => onOpenChange(false)}
          />
        )}
        {exitType === "kullanim" && (
          <UsageForm
            products={products}
            currency={currency}
            onDone={async () => {
              onOpenChange(false);
              await onSaved();
            }}
            onCancel={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// --------------------------- SATIŞ (şartname #17) ---------------------------
function SaleForm({
  products,
  customers,
  onCustomersChanged,
  presetCustomerId,
  currency,
  onDone,
  onCancel,
}) {
  const inStock = products.filter((p) => (p.quantity || 0) > 0);
  // Müşteri profilinden gelindiyse hazır seçili başlar (kullanıcı değiştirebilir).
  const [customerId, setCustomerId] = useState(presetCustomerId || "");
  const [vehicleId, setVehicleId] = useState("");
  const [saleDate, setSaleDate] = useState(TODAY());
  const [lines, setLines] = useState([{ product_id: "", quantity: 1, price: emptyPrice() }]);
  const [service, setService] = useState({
    enabled: false,
    description: "",
    ...emptyPrice(),
  });
  const [collections, setCollections] = useState([{ method: "nakit", amount: "" }]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const productItems = inStock.map((p) => ({
    value: p.id,
    label: `${p.name} — stok ${p.quantity}`,
    keywords: [p.name, p.brand, p.size, p.supplier_name].filter(Boolean),
  }));

  // Seçili müşterinin araçları. Birden fazla araç varsa hangi araç için satış
  // yapıldığı sorulur; tek araçta seçim gösterilmez (backend o aracı yazar).
  const selectedCustomer = customers.find((c) => c.id === customerId);
  const customerVehicles = (selectedCustomer?.vehicles || []).filter(
    (v) => v.plate || v.brand || v.model
  );
  const needsVehicleChoice = customerVehicles.length > 1;
  const vehicleLabel = (v) =>
    [v.plate, [v.brand, v.model].filter(Boolean).join(" ")].filter(Boolean).join(" — ") ||
    "Plakasız araç";

  // Müşteri değişince araç seçimi sıfırlanır.
  useEffect(() => {
    setVehicleId("");
  }, [customerId]);

  const setLine = (idx, patch) =>
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  /** Ürün seçildiğinde hedeflenen satış fiyatı otomatik gelir (değiştirilebilir). */
  const pickProduct = (idx, productId) => {
    const p = inStock.find((x) => x.id === productId);
    setLine(idx, {
      product_id: productId,
      price: p
        ? {
            amount: String(p.sale_price_input || ""),
            kdv_included: !!p.sale_kdv_included,
            kdv_rate: p.sale_kdv_rate ?? DEFAULT_KDV,
          }
        : emptyPrice(),
    });
  };

  const addLine = () =>
    setLines((prev) => [...prev, { product_id: "", quantity: 1, price: emptyPrice() }]);
  const removeLine = (idx) =>
    setLines((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const itemsGross = lines.reduce((acc, l) => {
    const { gross } = vatSplit(l.price.amount, l.price.kdv_rate, l.price.kdv_included);
    return acc + gross * (Number(l.quantity) || 0);
  }, 0);
  const serviceGross = service.enabled
    ? vatSplit(service.amount, service.kdv_rate, service.kdv_included).gross
    : 0;
  const totalGross = round2(itemsGross + serviceGross);
  const collected = round2(
    collections.reduce((a, c) => a + (Number(c.amount) || 0), 0)
  );
  const diff = round2(totalGross - collected);
  const balanced = Math.abs(diff) < 0.005;

  const addCollection = () =>
    setCollections((prev) => [...prev, { method: "nakit", amount: "" }]);
  const removeCollection = (idx) =>
    setCollections((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));
  const setCollection = (idx, patch) =>
    setCollections((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));

  const submit = async (e) => {
    e.preventDefault();
    if (!customerId) {
      toast.error("Müşteri seçin");
      return;
    }
    if (needsVehicleChoice && !vehicleId) {
      toast.error("Araç seçin");
      return;
    }
    if (lines.some((l) => !l.product_id)) {
      toast.error("Her satır için ürün seçin");
      return;
    }
    if (!balanced) {
      toast.error("Tahsilat toplamı satış toplamına eşit olmalıdır");
      return;
    }
    setSaving(true);
    try {
      await api.post("/sales-stock/sales", {
        customer_id: customerId,
        vehicle_id: vehicleId || null,
        sale_date: saleDate || null,
        items: lines.map((l) => ({
          product_id: l.product_id,
          quantity: Number(l.quantity) || 1,
          unit_price: Number(l.price.amount) || 0,
          kdv_included: !!l.price.kdv_included,
          kdv_rate: Number(l.price.kdv_rate),
        })),
        service: {
          enabled: !!service.enabled,
          description: service.description.trim() || null,
          amount: Number(service.amount) || 0,
          kdv_included: !!service.kdv_included,
          kdv_rate: Number(service.kdv_rate),
        },
        collections: collections.map((c) => ({
          method: c.method,
          amount: Number(c.amount) || 0,
        })),
        notes: notes.trim() || null,
      });
      toast.success("Satış kaydedildi");
      await onDone();
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="sale-form">
      {/* Müşteri seçimi + "Ekle" butonu tam genişlikte kendi satırında durur —
          uzun müşteri adları seçildiğinde buton yandaki tarih alanının altına
          kaymasın. */}
      <CustomerPicker
        customers={customers}
        value={customerId}
        onChange={setCustomerId}
        onCreated={async (id) => {
          await onCustomersChanged(id);
          setCustomerId(id);
        }}
      />

      <div className="grid sm:grid-cols-2 gap-3">
        {needsVehicleChoice && (
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Araç *</Label>
            <Select value={vehicleId} onValueChange={setVehicleId}>
              <SelectTrigger className="rounded-sm" data-testid="sale-vehicle-select">
                <SelectValue placeholder="Araç seçin" />
              </SelectTrigger>
              <SelectContent>
                {customerVehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {vehicleLabel(v)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground">
              Bu müşterinin {customerVehicles.length} aracı var — satışın hangi
              araç için yapıldığını seçin.
            </p>
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Çıkış Tarihi *</Label>
          <Input
            type="date"
            value={saleDate}
            onChange={(e) => setSaleDate(e.target.value)}
            className="rounded-sm"
            data-testid="sale-date"
          />
        </div>
      </div>

      {inStock.length === 0 && (
        <div className="border-l-2 border-amber-500 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Stokta satılabilir ürün yok. Önce stok girişi yapın.
        </div>
      )}

      <div className="space-y-3">
        {lines.map((l, i) => {
          const selected = inStock.find((p) => p.id === l.product_id);
          return (
            <Card key={i} className="rounded-sm border-border p-3 space-y-3" data-testid={`sale-line-${i}`}>
              <div className="grid sm:grid-cols-[1fr_100px_auto] gap-3 items-end">
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">Ürün *</Label>
                  <Combobox
                    value={l.product_id}
                    onChange={(v) => pickProduct(i, v)}
                    items={productItems}
                    placeholder="Ürün seçin"
                    searchPlaceholder="Ürün, marka, ebat ara..."
                    emptyText="Stokta ürün bulunamadı"
                    testId={`sale-line-product-${i}`}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">Adet *</Label>
                  <Input
                    type="number"
                    min="1"
                    max={selected?.quantity || undefined}
                    value={l.quantity}
                    onChange={(e) => setLine(i, { quantity: e.target.value })}
                    className="rounded-sm font-mono"
                    data-testid={`sale-line-qty-${i}`}
                  />
                </div>
                {lines.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-sm border-destructive/40 text-destructive hover:bg-destructive/10 mb-0.5"
                    onClick={() => removeLine(i)}
                    data-testid={`sale-line-remove-${i}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              <PriceFields
                label="Satış Fiyatı (birim)"
                value={l.price}
                onChange={(v) => setLine(i, { price: v })}
                currency={currency}
                testId={`sale-line-price-${i}`}
              />
              {selected && (
                <div className="text-[11px] text-muted-foreground font-mono">
                  Stok: {selected.quantity} • Hedeflenen satış:{" "}
                  {fmtMoney(selected.sale_price_gross, currency)} (KDV dahil)
                </div>
              )}
            </Card>
          );
        })}
      </div>

      <Button
        type="button"
        variant="outline"
        onClick={addLine}
        className="rounded-sm w-full"
        data-testid="sale-add-line"
      >
        <Plus className="h-4 w-4 mr-2" /> Ürün Ekle
      </Button>

      {/* Hizmet bedeli */}
      <Card className="rounded-sm border-border p-3 space-y-3">
        <div className="flex items-center gap-2">
          <Checkbox
            id="service-enabled"
            checked={service.enabled}
            onCheckedChange={(v) => setService({ ...service, enabled: v === true })}
            data-testid="sale-service-enabled"
          />
          <Label htmlFor="service-enabled" className="text-sm font-normal cursor-pointer">
            Hizmet / işçilik bedeli ekle
          </Label>
        </div>
        {service.enabled && (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Açıklama</Label>
              <Input
                value={service.description}
                onChange={(e) => setService({ ...service, description: e.target.value })}
                placeholder="Montaj, balans, ..."
                className="rounded-sm"
                data-testid="sale-service-description"
              />
            </div>
            <PriceFields
              label="Hizmet / İşçilik Tutarı"
              value={service}
              onChange={(v) => setService({ ...service, ...v })}
              currency={currency}
              testId="sale-service"
            />
          </div>
        )}
      </Card>

      {/* Tahsilat */}
      <Card className="rounded-sm border-border p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div className="label-eyebrow">Tahsilat Şekli</div>
          <div className="text-xs text-muted-foreground">
            Bir satışta birden fazla tahsilat şekli kullanılabilir
          </div>
        </div>
        {collections.map((c, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end" data-testid={`collection-${i}`}>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Şekil</Label>
              <Select value={c.method} onValueChange={(v) => setCollection(i, { method: v })}>
                <SelectTrigger className="rounded-sm" data-testid={`collection-method-${i}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Tutar</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={c.amount}
                onChange={(e) => setCollection(i, { amount: e.target.value })}
                className="rounded-sm font-mono"
                data-testid={`collection-amount-${i}`}
              />
            </div>
            {collections.length > 1 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-sm border-destructive/40 text-destructive hover:bg-destructive/10 mb-0.5"
                onClick={() => removeCollection(i)}
                data-testid={`collection-remove-${i}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        ))}
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addCollection}
            className="rounded-sm"
            data-testid="collection-add"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Tahsilat Ekle
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() =>
              setCollections([{ method: collections[0]?.method || "nakit", amount: String(totalGross) }])
            }
            className="rounded-sm"
            data-testid="collection-fill"
          >
            Tümünü tek şekle ata
          </Button>
        </div>
      </Card>

      {/* Toplam */}
      <div className="border-t border-border pt-3 space-y-1.5">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Ürünler (KDV dahil)</span>
          <span className="font-mono">{fmtMoney(round2(itemsGross), currency)}</span>
        </div>
        {service.enabled && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Hizmet Bedeli (KDV dahil)</span>
            <span className="font-mono">{fmtMoney(round2(serviceGross), currency)}</span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="label-eyebrow">Ödeme Tutarı (KDV dahil)</span>
          <span className="font-display font-black text-2xl tracking-tighter" data-testid="sale-total">
            {fmtMoney(totalGross, currency)}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Tahsilat Toplamı</span>
          <span className={`font-mono font-bold ${balanced ? "text-emerald-700" : "text-destructive"}`} data-testid="sale-collected">
            {fmtMoney(collected, currency)}
          </span>
        </div>
        {!balanced && (
          <div
            className="border-l-2 border-destructive bg-destructive/10 px-3 py-2 text-xs text-destructive"
            data-testid="sale-balance-warning"
          >
            Tahsilat toplamı satış toplamına eşit değil. Fark:{" "}
            <b>{fmtMoney(Math.abs(diff), currency)}</b> {diff > 0 ? "eksik" : "fazla"}
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        <Label className="label-eyebrow">Not</Label>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-sm" rows={2} />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-sm">
          İptal
        </Button>
        <Button
          type="submit"
          disabled={
            saving ||
            !balanced ||
            inStock.length === 0 ||
            (needsVehicleChoice && !vehicleId)
          }
          className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
          data-testid="sale-submit"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Satışı Kaydet"}
        </Button>
      </DialogFooter>
    </form>
  );
}

// ------------------------ ÜRÜN İADE (şartname #18) ------------------------
function ReturnForm({ suppliers, entries, currency, onDone, onCancel }) {
  const [supplierId, setSupplierId] = useState("");
  const [exitDate, setExitDate] = useState(TODAY());
  const [quantities, setQuantities] = useState({});
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const supplierItems = suppliers.map((s) => ({
    value: s.id,
    label: s.phone ? `${s.full_name} — ${s.phone}` : s.full_name,
    keywords: [s.full_name, s.phone].filter(Boolean),
  }));

  // Satıcı seçildiğinde o satıcıya ait stok girişleri ve ürünleri listelenir.
  const supplierEntries = entries.filter((e) => e.supplier_id === supplierId);

  const submit = async (e) => {
    e.preventDefault();
    if (!supplierId) {
      toast.error("Satıcı seçin");
      return;
    }
    const items = Object.entries(quantities)
      .map(([product_id, quantity]) => ({ product_id, quantity: Number(quantity) || 0 }))
      .filter((it) => it.quantity > 0);
    if (items.length === 0) {
      toast.error("İade edilecek ürün ve adet girin");
      return;
    }
    setSaving(true);
    try {
      await api.post("/sales-stock/returns", {
        supplier_id: supplierId,
        exit_date: exitDate || null,
        items,
        notes: notes.trim() || null,
      });
      toast.success("İade kaydedildi, stok güncellendi");
      await onDone();
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="return-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Satıcı *</Label>
          <Combobox
            value={supplierId}
            onChange={(v) => {
              setSupplierId(v);
              setQuantities({});
            }}
            items={supplierItems}
            placeholder="Satıcı seçin"
            searchPlaceholder="Satıcı ara..."
            emptyText="Satıcı bulunamadı"
            testId="return-supplier-select"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Çıkış Tarihi *</Label>
          <Input
            type="date"
            value={exitDate}
            onChange={(e) => setExitDate(e.target.value)}
            className="rounded-sm"
            data-testid="return-date"
          />
        </div>
      </div>

      <div className="border-l-2 border-sky-500 bg-sky-50 px-4 py-2 text-xs text-sky-900">
        İade işleminde tutar girişi yapılmaz — yalnızca iade edilen adet girilir ve
        stoktan düşülür.
      </div>

      {!supplierId ? (
        <p className="text-sm text-muted-foreground text-center py-6">
          İade edilecek ürünleri görmek için satıcı seçin.
        </p>
      ) : supplierEntries.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">
          Bu satıcıya ait stok girişi bulunamadı.
        </p>
      ) : (
        <div className="space-y-3" data-testid="return-entries">
          {supplierEntries.map((e) => (
            <Card key={e.id} className="rounded-sm border-border overflow-hidden">
              <div className="px-4 py-2 bg-muted/50 text-xs font-mono">
                Fatura: {e.invoice_no || "—"}
                {e.invoice_date
                  ? ` • ${new Date(e.invoice_date).toLocaleDateString("tr-TR")}`
                  : ""}
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left px-4 py-2 label-eyebrow">Ürün</th>
                    <th className="text-right px-4 py-2 label-eyebrow">Giriş</th>
                    <th className="text-right px-4 py-2 label-eyebrow">Mevcut</th>
                    <th className="text-right px-4 py-2 label-eyebrow">Alış (KDV dahil)</th>
                    <th className="text-right px-4 py-2 label-eyebrow w-32">İade Adedi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {(e.items || []).map((it) => (
                    <tr key={it.id} data-testid={`return-item-${it.id}`}>
                      <td className="px-4 py-2">{it.name}</td>
                      <td className="px-4 py-2 font-mono text-right">{it.entry_quantity}</td>
                      <td className="px-4 py-2 font-mono text-right font-bold">{it.quantity}</td>
                      <td className="px-4 py-2 font-mono text-right">
                        {fmtMoney(it.purchase_price_gross, currency)}
                      </td>
                      <td className="px-4 py-2">
                        <Input
                          type="number"
                          min="0"
                          max={it.quantity}
                          value={quantities[it.id] ?? ""}
                          disabled={it.quantity <= 0}
                          onChange={(ev) =>
                            setQuantities((prev) => ({ ...prev, [it.id]: ev.target.value }))
                          }
                          className="rounded-sm font-mono h-8 text-right"
                          placeholder="0"
                          data-testid={`return-qty-${it.id}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ))}
        </div>
      )}

      <div className="space-y-1.5">
        <Label className="label-eyebrow">Not</Label>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-sm" rows={2} />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-sm">
          İptal
        </Button>
        <Button
          type="submit"
          disabled={saving || !supplierId}
          className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
          data-testid="return-submit"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "İadeyi Kaydet"}
        </Button>
      </DialogFooter>
    </form>
  );
}

// ------------------------- KULLANIM (şartname #19) -------------------------
function UsageForm({ products, currency, onDone, onCancel }) {
  const inStock = products.filter((p) => (p.quantity || 0) > 0);
  const [productId, setProductId] = useState("");
  const [exitDate, setExitDate] = useState(TODAY());
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const selected = inStock.find((p) => p.id === productId);

  const items = inStock.map((p) => ({
    value: p.id,
    label: `${p.name} — stok ${p.quantity}`,
    keywords: [p.name, p.brand, p.size].filter(Boolean),
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!productId) {
      toast.error("Ürün seçin");
      return;
    }
    setSaving(true);
    try {
      await api.post("/sales-stock/usages", {
        product_id: productId,
        quantity: Number(quantity) || 1,
        exit_date: exitDate || null,
        notes: notes.trim() || null,
      });
      toast.success("Kullanım kaydedildi, stok güncellendi");
      await onDone();
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="usage-form">
      <div className="border-l-2 border-sky-500 bg-sky-50 px-4 py-2 text-xs text-sky-900">
        Kullanım işleminde tutar girişi yapılmaz — yalnızca kullanılan adet girilir
        ve stoktan düşülür.
      </div>

      <div className="space-y-1.5">
        <Label className="label-eyebrow">İlgili Ürün *</Label>
        <Combobox
          value={productId}
          onChange={setProductId}
          items={items}
          placeholder="Ürün seçin"
          searchPlaceholder="Ürün, marka, ebat ara..."
          emptyText="Stokta ürün bulunamadı"
          testId="usage-product-select"
        />
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Çıkış Tarihi *</Label>
          <Input
            type="date"
            value={exitDate}
            onChange={(e) => setExitDate(e.target.value)}
            className="rounded-sm"
            data-testid="usage-date"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Stoktaki Miktar</Label>
          <Input
            value={selected ? selected.quantity : "—"}
            readOnly
            disabled
            className="rounded-sm font-mono bg-muted"
            data-testid="usage-stock"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Ürün Fiyatı</Label>
          <Input
            value={selected ? fmtMoney(selected.purchase_price_gross, currency) : "—"}
            readOnly
            disabled
            className="rounded-sm font-mono bg-muted"
            data-testid="usage-price"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="label-eyebrow">Kullanım Adedi *</Label>
        <Input
          type="number"
          min="1"
          max={selected?.quantity || undefined}
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          className="rounded-sm font-mono"
          data-testid="usage-quantity"
        />
        {selected && Number(quantity) > selected.quantity && (
          <p className="text-xs text-destructive">
            Stok miktarından fazla kullanım yapılamaz (mevcut: {selected.quantity}).
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label className="label-eyebrow">Not</Label>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-sm" rows={2} />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-sm">
          İptal
        </Button>
        <Button
          type="submit"
          disabled={
            saving ||
            !productId ||
            (selected && Number(quantity) > selected.quantity)
          }
          className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
          data-testid="usage-submit"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kullanımı Kaydet"}
        </Button>
      </DialogFooter>
    </form>
  );
}

// ===========================================================================
// ÜRÜN DÜZENLEME (satış fiyatı / kritik stok / depo)
// ===========================================================================
function ProductEditDialog({ product, onClose, warehouses, currency, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (product) {
      setForm({
        // Durum: eski kayıtlarda NULL olabilir → "" ile "seçilmemiş" gösterilir
        // ve kullanıcı seçmedikçe gönderilmez (var olan NULL korunur).
        condition: product.condition || "",
        critical_quantity: product.critical_quantity ?? 0,
        warehouse_id: product.warehouse_id || "",
        sale: {
          amount: String(product.sale_price_input ?? ""),
          kdv_included: !!product.sale_kdv_included,
          kdv_rate: product.sale_kdv_rate ?? DEFAULT_KDV,
        },
        use_profit_rate: product.profit_rate != null,
        profit_rate: product.profit_rate != null ? String(product.profit_rate) : "",
        photo_media_id: product.photo_media_id || "",
        photo_preview: "",
        // Yalnızca çıkış hareketi yokken düzenlenebilir
        quantity: String(product.quantity ?? 0),
        purchase: {
          amount: String(product.purchase_price_input ?? ""),
          kdv_included: !!product.purchase_kdv_included,
          kdv_rate: product.purchase_kdv_rate ?? DEFAULT_KDV,
        },
      });
    } else {
      setForm(null);
    }
  }, [product]);

  if (!product || !form) return null;

  // Çıkış hareketi yoksa adet ve alış fiyatı da düzeltilebilir.
  // Backend aynı kuralı uyguluyor (çıkış varsa 409) — bu yalnızca arayüz.
  const canModify = !!product.can_modify;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        critical_quantity: Number(form.critical_quantity) || 0,
        warehouse_id: form.warehouse_id || null,
        sale: {
          amount: Number(form.sale.amount) || 0,
          kdv_included: !!form.sale.kdv_included,
          kdv_rate: Number(form.sale.kdv_rate),
        },
        profit_rate: form.use_profit_rate ? Number(form.profit_rate) || 0 : null,
        // "" gönderilirse backend fotoğrafı kaldırır (bkz. ProductUpdateIn).
        photo_media_id: form.photo_media_id || "",
      };
      // Durum yalnızca SEÇİLİYSE gönderilir; boş bırakılırsa backend alana
      // dokunmaz (ProductUpdateIn.condition None → güncelleme yok).
      if (form.condition) payload.condition = form.condition;
      if (canModify) {
        payload.quantity = Number(form.quantity) || 0;
        payload.purchase = {
          amount: Number(form.purchase.amount) || 0,
          kdv_included: !!form.purchase.kdv_included,
          kdv_rate: Number(form.purchase.kdv_rate),
        };
      }
      await api.put(`/sales-stock/products/${product.id}`, payload);
      toast.success("Ürün güncellendi");
      onClose();
      await onSaved();
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">{product.name}</DialogTitle>
          <DialogDescription>
            Stok miktarı buradan değiştirilemez; yalnızca stok hareketleriyle değişir.
            Alış fiyatı ve alış KDV oranı geçmişi bozmamak için sabittir.
            <br />
            <span
              className={canModify ? "text-emerald-700 font-medium" : "text-amber-700 font-medium"}
              data-testid="product-edit-modify-note"
            >
              Çıkış işlemi yapılmadıysa kayıt silinebilir/güncellenebilir.
            </span>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="product-edit-form">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="border border-border rounded-sm p-3">
              <div className="label-eyebrow">Mevcut Stok</div>
              {canModify ? (
                <Input
                  type="number"
                  min="0"
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                  className="rounded-sm font-mono mt-1 h-8"
                  data-testid="product-edit-quantity"
                />
              ) : (
                <div className="font-display font-bold text-xl mt-1">{product.quantity}</div>
              )}
            </div>
            <div className="border border-border rounded-sm p-3">
              <div className="label-eyebrow">Alış (KDV dahil)</div>
              <div className="font-mono font-bold text-base mt-1">
                {fmtMoney(product.purchase_price_gross, currency)}
              </div>
              <div className="text-[10px] text-muted-foreground">
                KDV %{product.purchase_kdv_rate}
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {/* Durum (Sıfır / İkinci El) — Product.condition alanını günceller */}
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Durum</Label>
              <Select
                value={form.condition}
                onValueChange={(v) => setForm({ ...form, condition: v })}
              >
                <SelectTrigger className="rounded-sm" data-testid="product-edit-condition">
                  <SelectValue placeholder="Seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sifir">Sıfır</SelectItem>
                  <SelectItem value="ikinci_el">İkinci El</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Kritik Stok Miktarı</Label>
              <Input
                type="number"
                min="0"
                value={form.critical_quantity}
                onChange={(e) => setForm({ ...form, critical_quantity: e.target.value })}
                className="rounded-sm font-mono"
                data-testid="product-edit-critical"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Depo</Label>
              <Select
                value={form.warehouse_id}
                onValueChange={(v) => setForm({ ...form, warehouse_id: v })}
              >
                <SelectTrigger className="rounded-sm" data-testid="product-edit-warehouse">
                  <SelectValue placeholder="Seçin" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Alış fiyatı normalde sabittir; çıkış hareketi yoksa hatalı giriş
              düzeltilebilsin diye açılır (backend aynı kuralı uygular). */}
          {canModify && (
            <PriceFields
              label="Alış Fiyatı (birim)"
              value={form.purchase}
              onChange={(v) => setForm({ ...form, purchase: v })}
              currency={currency}
              testId="product-edit-purchase"
            />
          )}

          <PhotoPicker
            value={form.photo_media_id}
            preview={form.photo_preview}
            onChange={(patch) => setForm({ ...form, ...patch })}
            testId="product-edit-photo"
          />

          <PriceFields
            label="Hedeflenen Satış Fiyatı (birim)"
            value={form.sale}
            onChange={(v) => setForm({ ...form, sale: v })}
            currency={currency}
            testId="product-edit-sale"
            disabled={form.use_profit_rate}
          />
          <div className="flex items-center gap-2">
            <Checkbox
              id="edit-profit"
              checked={form.use_profit_rate}
              onCheckedChange={(v) => setForm({ ...form, use_profit_rate: v === true })}
              data-testid="product-edit-use-profit"
            />
            <Label htmlFor="edit-profit" className="text-xs font-normal cursor-pointer">
              Alış fiyatı üzerinden yüzde kâr oranı kullan
            </Label>
          </div>
          {form.use_profit_rate && (
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Kâr Oranı (%)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.profit_rate}
                onChange={(e) => setForm({ ...form, profit_rate: e.target.value })}
                className="rounded-sm font-mono w-32"
                data-testid="product-edit-profit-rate"
              />
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-sm">
              İptal
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
              data-testid="product-edit-submit"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
